import React from "react";
import EmailSignature from "../shared/EmailSignature";
import { truncateWords, emailExpertProfileUrl, EXPERT_DESCRIPTION_WORD_LIMIT } from "../expert_picker/expertProfile";

// Template badge component for placeholder content in email preview
function TemplateBadge({ children }) {
  return (
    <span className="badge rounded-pill text-dark fw-normal" style={{ backgroundColor: "#e5e5e5", fontSize: "0.85em" }}>
      {children}
    </span>
  );
}

// User input text component with highlighting
function UserInputText({ children, fontSize }) {
  const style = {
    backgroundColor: "#ffffc3",
    borderStyle: "dashed",
    borderColor: "#eeee10",
    ...(fontSize && { fontSize }),
  };
  return <span style={style}>{children}</span>;
}

// Email paragraph component with consistent spacing
function EmailPara({ children }) {
  return <div style={{ marginTop: "2em", marginBottom: "2em" }}>{children}</div>;
}

// Email preview component showing how the story idea will appear in the email.
//
// `selectedExperts`: array of `{ id, name, description }` from the ExpertPicker.
// When non-empty, real names + descriptions render in the same yellow
// editable-placeholder treatment as the title / news hook / topics. When
// empty, the current grey "Expert N name / bio" placeholders are kept.
export default function EmailPreview({
  storyIdea,
  marketingPersona = null,
  selectedExperts = [],
  maxWidth = null,
  width = null,
}) {
  const containerStyle = {
    fontSize: "0.9em",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    boxShadow: "10px 5px 5px rgb(0 0 0 / 10%)",
  };

  // Add width constraints if provided (for bulk modal)
  if (maxWidth) containerStyle.maxWidth = maxWidth;
  if (width) containerStyle.width = width;

  const fromName = marketingPersona
    ? `${marketingPersona.first_name} ${marketingPersona.last_name}`
    : "[Marketing Persona]";

  const fromEmail = marketingPersona?.email || "persona@example.com";

  return (
    <div className="border rounded p-4 bg-white" style={containerStyle}>
      <div className="d-flex my-1">
        <div className="fw-bold me-2" style={{ minWidth: "70px" }}>
          From:
        </div>
        <div className="flex-grow-1">
          <UserInputText fontSize="0.85em">
            {fromName} at Qwoted &lt;{fromEmail}&gt;
          </UserInputText>
        </div>
      </div>
      <div className="d-flex my-2">
        <div className="fw-bold me-2" style={{ minWidth: "70px" }}>
          Subject:
        </div>
        <div className="flex-grow-1">
          <UserInputText>{storyIdea.title || "Your title"}</UserInputText>
        </div>
      </div>
      <hr className="my-3" />
      <div className="m-3">
        <div style={{ lineHeight: "2" }}>
          <EmailPara>
            Hi <TemplateBadge>name</TemplateBadge>,
          </EmailPara>

          <EmailPara>
            <UserInputText>{storyIdea.news_hook || "Your news hook"}</UserInputText>
          </EmailPara>

          <EmailPara>
            <TemplateBadge>These experts could contribute on pieces about...</TemplateBadge>
            <span style={{ paddingLeft: "0.5em" }}>
              {storyIdea.topics.length > 0 ? (
                storyIdea.topics.map((topic, index, array) => (
                  <span key={topic}>
                    <UserInputText>{topic}</UserInputText>
                    {index < array.length - 1 && (index === array.length - 2 ? " and " : ", ")}
                  </span>
                ))
              ) : (
                <UserInputText>your topics</UserInputText>
              )}
            </span>
            <ul className="mt-2">
              {selectedExperts.length > 0
                ? selectedExperts.map((expert) => {
                    const profileUrl = emailExpertProfileUrl(expert);
                    const { text: bioText, truncated } = truncateWords(
                      expert.description,
                      EXPERT_DESCRIPTION_WORD_LIMIT,
                    );
                    return (
                      <li key={expert.id}>
                        <b>
                          <UserInputText>
                            {profileUrl ? (
                              <a href={profileUrl} target="_blank" rel="noreferrer">
                                {expert.name}
                              </a>
                            ) : (
                              expert.name
                            )}
                          </UserInputText>
                        </b>{" "}
                        <UserInputText>
                          {bioText || "(no bio)"}
                          {truncated && profileUrl && (
                            <>
                              {" "}
                              <a href={profileUrl} target="_blank" rel="noreferrer">
                                Read more
                              </a>
                            </>
                          )}
                        </UserInputText>
                      </li>
                    );
                  })
                : [1, 2, 3].map((n) => (
                    <li key={n}>
                      <b>
                        <UserInputText>{`Expert ${n} name`}</UserInputText>
                      </b>{" "}
                      <UserInputText>{`Expert ${n} bio`}</UserInputText>
                    </li>
                  ))}
            </ul>
          </EmailPara>

          <EmailPara>
            <TemplateBadge>Closing para</TemplateBadge>
          </EmailPara>
        </div>

        {marketingPersona ? (
          <EmailSignature
            name={`${marketingPersona.first_name} ${marketingPersona.last_name}`}
            title="Media Success Team"
            email={marketingPersona.email}
          />
        ) : (
          <EmailPara>
            <TemplateBadge>Signature</TemplateBadge>
          </EmailPara>
        )}
      </div>
    </div>
  );
}
